import {
  AWTLogManager,
  AWTLogConfiguration,
  AWTEventData,
  AWTEventDataWithMetaData,
  AWTEventsDroppedReason,
  AWTEventsRejectedReason
} from '@aria/webjs-sdk';
import AWTTransmissionManagerCore from '@aria/webjs-sdk/dist/src/webjs/AWTTransmissionManagerCore';
import AWTQueueManager from '@aria/webjs-sdk/dist/src/webjs/AWTQueueManager';
import { DataField, TelemetryEvent, logNotification, LogLevel, Category } from '@ms/oteljs';
import { getAriaEvent } from './GetAriaEvent';

// Constants
const ARIA_INIT_TOKEN = 'cd836626611c4caaa8fc5b2e728ee81d-3b6d6c45-6377-4bf5-9792-dbf8e1881088-7521'; // Office.System

// Expose Aria stats for easy consumption
export let otelEventsProcessed: number = 0;
export let ariaEventsSent: number = 0;
export let ariaEventsDropped: number = 0;
export let ariaEventsRejected: number = 0;
export let ariaEventsRetrying: number = 0;

// State
let awtInitialized: boolean = false;

export function sendEvent(telemetryEvent: TelemetryEvent, additionalDataFields: DataField[], timestamp?: number) {
  otelEventsProcessed++;

  initialize();
  let ariaEvent: AWTEventData;

  if (!telemetryEvent.telemetryProperties || !telemetryEvent.telemetryProperties.ariaTenantToken) {
    throw new Error('Unable to find ariaTenantToken for namespace.');
  }

  ariaEvent = getAriaEvent(telemetryEvent, additionalDataFields, timestamp);

  let logger = AWTLogManager.getLogger(telemetryEvent.telemetryProperties.ariaTenantToken);
  logger.logEvent(ariaEvent);
}

export function initialize(configuration?: AWTLogConfiguration) {
  if (!awtInitialized) {
    hookUpAriaNotifications();
    AWTLogManager.initialize(ARIA_INIT_TOKEN, configuration);
    awtInitialized = true;
  }
}

function hookUpAriaNotifications() {
  AWTLogManager.addNotificationListener({
    eventsSent: (events: AWTEventDataWithMetaData[]) => {
      logNotification(LogLevel.Info, Category.Transport, () => {
        return `Successfully sent ${events.length} event(s)`;
      });
      logNotification(LogLevel.Verbose, Category.Transport, () => {
        return `Sent event(s) details : ${JSON.stringify(events, null, 2)}`;
      });
      ariaEventsSent += events.length;
    },
    eventsDropped: (events: AWTEventDataWithMetaData[], reason: AWTEventsDroppedReason) => {
      logNotification(LogLevel.Error, Category.Transport, () => {
        return `Dropped ${events.length} event(s) because ${reason}`;
      });
      logNotification(LogLevel.Verbose, Category.Transport, () => {
        return `Dropped event(s) details : ${JSON.stringify(events, null, 2)}`;
      });
      ariaEventsDropped += events.length;
    },
    eventsRejected: (events: AWTEventDataWithMetaData[], reason: AWTEventsRejectedReason) => {
      logNotification(LogLevel.Error, Category.Transport, () => {
        return `Rejected ${events.length} event(s) because ${reason}`;
      });
      logNotification(LogLevel.Verbose, Category.Transport, () => {
        return `Rejected event(s) details : ${JSON.stringify(events, null, 2)}`;
      });
      ariaEventsRejected += events.length;
    },
    eventsRetrying: (events: AWTEventDataWithMetaData[]) => {
      logNotification(LogLevel.Warning, Category.Transport, () => {
        return `Retrying ${events.length} event(s)`;
      });
      logNotification(LogLevel.Verbose, Category.Transport, () => {
        return `Retrying event(s) details : ${JSON.stringify(events, null, 2)}`;
      });
      ariaEventsRetrying += events.length;
    }
  });
}

/**
 * Aria SDK uses HMLT5 beacons API to send the last request to the server after it receives the shutdown call.
 * On browsers that don't support this API, it uses the traditional synchronous http POST request. The beacons
 * API is supposed to be the new hotness that allows developers to *reliably* and *asynchronously* send http requests
 * to the server during shutdown - attempting to send a generic asynchronous http request will not work reliably,
 * and sending data synchronously can be interrupted by the browser since the webpage is given a limited amount of
 * time to complete its tasks.
 * Unfortunately the API is behaving in strange ways and not sending http requests in some cases.
 * Adding this method to temporarily disable the API while we investigate the root cause.
 * VSO.2599234: Aria's shutdown beacons request is not getting honored
 * @returns - whether or not the override was successful
 */
export function disableBeaconsApiAvailabilityForAriaSdk(): boolean {
  let ariaEventHander = AWTTransmissionManagerCore.getEventsHandler();
  if (ariaEventHander instanceof AWTQueueManager) {
    let ariaHttpManager: any = ariaEventHander._httpManager;
    if (ariaHttpManager && ariaHttpManager.hasOwnProperty('_useBeacons')) {
      ariaHttpManager._useBeacons = false;
      return true;
    }
  }

  return false;
}

export function shutdown() {
  AWTLogManager.flushAndTeardown();
}
